import {DatePipe, isPlatformBrowser} from '@angular/common';
import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { PAGES_AND_PATHS } from 'src/app/constants/pages-and-paths';
import { ShareEntitiesEnums } from 'src/app/enums/chat-enums';
import { AuthenticationHelperService } from 'src/app/services/helpers/authentication-helper.service';
import { ListsHelperService } from 'src/app/services/helpers/lists-helper.service';
import { Column, DeleteModalText, ListExtendedInterface, ListInterface, ListItem } from 'src/app/services/http-services/lists/lists-http-intterfaces';
import { ListsHttpService } from 'src/app/services/http-services/lists/lists-http.service';
import {UserRoles} from '../../../enums/general-enums';

@Component({
  selector: 'app-single-list',
  templateUrl: './single-list.component.html',
  providers: [DatePipe]
})
export class SingleListComponent implements OnInit, OnDestroy {
  listId = 0;
  list: ListInterface | undefined | ListExtendedInterface | any;
  tabSelected = '';
  PAGES_AND_PATHS = PAGES_AND_PATHS;
  isViewMoreModalOpen = false;
  isChangeVisibilityModalOpen = false;
  columns: Column[] = [];
  items: ListItem[] = [];
  refreshPageSubscription: Subscription | undefined;
  searchTerm = '';
  searchSubject = new Subject<string>();
  isDeleteModalOpen = false;
  modalText: DeleteModalText = { title: '', description: '', button: '' };
  loggedUser: any;
  isShareProfileModalOpen = false;
  listUrl = '';
  ShareEntitiesEnums = ShareEntitiesEnums;
  filterItemsSubscription: Subscription | undefined;
  filters = { sort: 'asc', id: 0 };
  isCopyListModalOpen = false;
  isReportModalOpen = false;
  isCopied = false;
  areColumnsIdeasOpen = false;
  isAiLoading = false;
  isAiError = false;
  selectedMedia: any = null;

  private readonly debounceTimeMs = 500;
  private toggleLike = new Subject<string>();

  constructor(
    private actRoute: ActivatedRoute,
    private listsHttpService: ListsHttpService,
    private listsHelperService: ListsHelperService,
    private toastService: ToastrService,
    private authenticationHelperService: AuthenticationHelperService,
    private router: Router,
    private meta: Meta,
    private title: Title,
    private datePipe: DatePipe,
    public cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.listUrl = window.location.href
        .replace('single-list', 'single-list-public')
        .replace('table', 'cards');
      this.loggedUser = this.authenticationHelperService.getUserData;
    }
    this.getParams();

    this.refreshPageSubscription = this.listsHelperService.refreshSingleListPage.subscribe(res => {
      if (res) {
        this.getSingleList();
        this.getPublicSingleList();
      }
    });

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((searchTerm: string) => this.getSingleList(searchTerm))
    ).subscribe((res: any) => {
      this.items = res.lyst_items;
      this.columns = res.attributes;
    });

    this.toggleLike.pipe(debounceTime(this.debounceTimeMs)).subscribe(() => {
      this.toggleLikeHttp();
    });

    this.filterItemsSubscription = this.listsHelperService.filterSingleListPage.subscribe((res: { sort: string, id: number }) => {
      if (res) {
        this.filters = res;
        this.getSingleList();
      }
    });
  }

  getParams(): void {
    this.actRoute.paramMap.subscribe(params => {
      this.listId = Number(params.get('id'));
      this.tabSelected = params.get('view') as string;
      this.getSingleList();
      this.getPublicSingleList();
    });
  }

  getSingleList(searchTerm?: string): Promise<any> {
    return (
      this.listsHttpService.getSingleList(this.listId, searchTerm, this.filters).then((res: ListInterface) => {
        if (res) {
          this.list = res;
        }
        this.items = res.lyst_items;
        this.columns = res.attributes;
        this.listsHelperService.refreshPage(false);

        return res;
      })
    );
  }

  getPublicSingleList(): void {
    this.listsHttpService.getPublicSingleList({search_keyword: this.searchTerm}, this.listId).then((res: ListInterface) => {
      const publicList = res;
      const dateObj = new Date(publicList.created_at);
      const formattedDate = this.datePipe.transform(dateObj, 'dd.MM.YYYY');

      this.title.setTitle(`${publicList.title} by ${publicList.user.name} - TheLysts`);

      this.meta.updateTag({
        name: 'description',
        content: `${publicList.title} by ${publicList.user.name} created on ${formattedDate} - TheLysts`
      });

      this.meta.updateTag({
        property: 'og:title',
        content: `${publicList.title} by ${publicList.user.name} - TheLysts`
      });

      this.meta.updateTag({
        property: 'twitter:title',
        content: `${publicList.title} by ${publicList.user.name} - TheLysts`
      });
      this.meta.updateTag({
        property: 'og:description',
        content: `${publicList.title} by ${publicList.user.name} created on ${formattedDate} - TheLysts`
      });
      this.meta.updateTag({
        property: 'twitter:description',
        content: `${publicList.title} by ${publicList.user.name} created on ${formattedDate} - TheLysts`
      });
      if (publicList.og_image_url) {
        this.meta.updateTag({
          property: 'og:image',
          content: publicList.og_image_url
        });
        this.meta.updateTag({
          property: 'twitter:image',
          content: publicList.og_image_url
        });
      } else {
        this.meta.updateTag({
          property: 'og:image',
          content: publicList.lyst_image_url !== null ? publicList.lyst_image_url : 'assets/images/og-image.png'
        });
        this.meta.updateTag({
          property: 'twitter:image',
          content: publicList.lyst_image_url !== null ? publicList.lyst_image_url : 'assets/images/og-image.png'
        });
      }
      this.meta.updateTag({
        property: 'og:url', content: window.location.origin + this.router.url
      });
    });
  }

  ngOnDestroy(): void {
    this.refreshPageSubscription?.unsubscribe();
    this.filterItemsSubscription?.unsubscribe();

    this.toggleLike.complete();
  }

  addListerToFavorites(): void {
    if (this.list.am_i_favorite_this_lyst) {
      this.list.favorite_users_count--;
    } else {
      this.list.favorite_users_count++;
    }
    this.list.am_i_favorite_this_lyst = !this.list.am_i_favorite_this_lyst;

    this.toggleLike.next();
  }

  toggleLikeHttp(): void {
    this.listsHttpService.addListToFavorites(this.list.id);
  }

  onSearchTermChanged(searchTerm: string): void{
    this.searchSubject.next(searchTerm);
  }

  openDeleteModal(): void {
    this.isDeleteModalOpen = true;
    this.modalText.title = 'LISTS.delete_list';
    this.modalText.description = 'Are you sure you wish to delete this lyst? This action cannot be undone.';
    this.modalText.button = 'GENERAL.delete';
  }

  deleteList(): void {
    this.isDeleteModalOpen = false;
    this.listsHttpService.deleteList(this.list.id).then((response: any) => {
      this.toastService.success(response.message);
      this.router.navigateByUrl('lists/my_lists');
    });
  }

  openDropdown(event: any): void {
    this.isViewMoreModalOpen = !this.isViewMoreModalOpen;
  }

  takeAction(): void {
    this.isCopyListModalOpen ? this.copyList() : this.changeVisibility();
  }

  changeVisibility(): void {
    this.listsHttpService.toggleListVisibilty(this.list.id).subscribe((res: any) => {
      this.toastService.success(res.message);
      this.isChangeVisibilityModalOpen = false;
      this.getSingleList();
    });
  }

  openVisibilityModal(): void {
    this.isChangeVisibilityModalOpen = true;
    const listVisibility = this.list.visibility ? 'private' : 'public';
    const modalDescription = this.list.visibility ? `Are you sure you want to make your list ${listVisibility}? No one will be able to view this list.` : `Are you sure you want to make your list ${listVisibility}? Anyone will be able to view this list.`;
    this.modalText.title = `Make list ${listVisibility}`;
    this.modalText.description = modalDescription;
    this.modalText.button = `Make ${listVisibility}`;
  }

  print(): void {
    this.listsHttpService.print(this.listId).subscribe((response: Blob) => {
      if (response.type === 'application/pdf') {
        const blobUrl = URL.createObjectURL(response);
        window.open(blobUrl, '_blank');
      }
    });
  }

  openCopyModal(): void {
    this.isCopyListModalOpen = true;
    this.modalText.title = `Copy this list?`;
    this.modalText.description = 'Do you wish to copy this list to your library?';
    this.modalText.button = `Yes, copy`;
  }

  copyList(): void {
    this.isCopyListModalOpen = false;
    this.listsHttpService.copyList(this.list.id).subscribe((response: any) => {
      this.isCopied = true;
      setTimeout(() => {
        this.isCopied = false;
      }, 3000);
      this.toastService.success(response.message);
      this.getSingleList();
    });
  }

  protected readonly UserRoles = UserRoles;
}
